import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { FormProvider, ValidationValueMessage, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import './widget.css';

import { EndType, DataPlaneMerchant, WidgetInputs } from '../../@types/types';
import {
	useErrorMessages,
	useFormFieldOptions,
	useFormSubmit,
	useWatchedSelections,
	useFormSelectOptions,
	useApplePay,
} from '../../hooks';
import { useOverflowing } from '../../hooks/overflowing';
import { useAgent } from '../../newrelic';
import {
	GiftType,
	GivingType,
	WidgetActions,
	PaymentLabels,
	dateFieldOptions,
	defaultFrequency,
	WidgetId,
	OpenIdConnectProviders,
} from '../../shared/constants';
import { ApplePayButton } from '../applePayButton/ApplePayButton';
import { Button } from '../button';
import { CustomFields } from '../customFields/custom-fields';
import { EngivenGiving } from '../engiven/engiven';
import ErrorMessage from '../errorMessage/error-message';
import { AmountField, TextField } from '../fields';
import { SelectField } from '../fields/select-field';
import { LineItems } from '../lineItems/line-items';
import Link from '../link/Link';
import { PoweredByPushpay } from '../pushpay/poweredByPushpay';
import { Recurring } from '../recurring/Recurring';
import Separator from '../separator/Separator';
import Success from '../success/success';
import TermsAndPolicy from '../terms/terms';

export type WidgetProps = { config: DataPlaneMerchant; handle: string; afterSubmitHandler?: (args: any) => void };

export const Widget: FC<WidgetProps> = ({ config, handle, afterSubmitHandler }) => {
	const agent = useAgent();
	const { t: translate } = useTranslation();
	const widgetRef = useRef<HTMLDivElement>(null);
	const scrollableRef = useRef<HTMLDivElement>(null);
	const amountInputRef = useRef<HTMLInputElement>(null);
	const shouldFocusAmountInputRef = useRef<boolean>(false);
	const [givingType, setGivingType] = useState(GivingType.CARD_OR_ACH);
	const {
		MerchantSettings: {
			PaymentLabel: PaymentLabelCode = 'give',
			PayerSpecifiedRecurringEndDateEnabled,
			RecurringByDefault,
			OneTimeFutureGiftEnabled,
			CurrencyCode,
			CurrencySymbol,
			CryptoLink,
			ApplePayMerchantName,
			SupportsApplePay,
			StockGivingLink,
			LegalName,
			SupportsMultiFundGiving,
			SupportsRockRmsSso,
		},
		DefaultFundKey,
		FundCustomLabel = '',
		Funds,
	} = config;

	const methods = useForm<WidgetInputs>({
		defaultValues: {
			Recurrence: defaultFrequency,
			FundKeyOrName: DefaultFundKey || undefined,
			GiftType: RecurringByDefault ? GiftType.RECURRING.toString() : GiftType.ONE_TIME.toString(),
			RecurringEndType: EndType.Never.toString(),
			RecurringStartDate: dateFieldOptions.min,
			GiftDate: OneTimeFutureGiftEnabled ? dateFieldOptions.min : undefined,
			OpenIdConnectProvider: SupportsRockRmsSso ? OpenIdConnectProviders.RockRms : undefined,
		},
	});

	const {
		watch,
		setError,
		register,
		setValue,
		handleSubmit,
		formState: { errors },
	} = methods;

	const paymentLabel = PaymentLabels[(PaymentLabelCode as any as string).toUpperCase() as keyof typeof PaymentLabels];
	const canApplePay = SupportsApplePay && !APP_SETTINGS.applePayDisabledForAll;
	const currentSelection = useWatchedSelections(watch, setValue, {
		hasApplePay: canApplePay,
	});
	const { applePayError, success, isAvailable, clearSuccess, makeApplePayment, clearAppleError } = useApplePay(
		config,
		canApplePay,
		paymentLabel
	);
	const { fieldOptions, customFieldKeys } = useFormFieldOptions({ config, currentSelection });

	const { funds, frequencyOptions, endTypeOptions, givingTypeOptions } = useFormSelectOptions({
		merchantSettings: config.MerchantSettings,
		paymentLabel,
		funds: Funds,
		hasDefaultFund: Boolean(DefaultFundKey),
		fundLabel: FundCustomLabel ?? undefined,
		showApplePay: isAvailable && currentSelection.showApplePay,
	});

	const { baseFields: errorMessages } = useErrorMessages(fieldOptions, errors);

	const onSubmit = useFormSubmit(handle, customFieldKeys, setError, afterSubmitHandler);

	const isOverflow = useOverflowing(scrollableRef);

	const onGivingChange = useCallback((evt: React.ChangeEvent<HTMLSelectElement>) => {
		switch (evt.target.value) {
			case GivingType.STOCK.toString():
				setGivingType(GivingType.STOCK);
				break;
			case GivingType.CRYPTOCURRENCY.toString():
				setGivingType(GivingType.CRYPTOCURRENCY);
				break;

			default:
				setGivingType(GivingType.CARD_OR_ACH);
				break;
		}
	}, []);

	const { hasOtherGiving, isCardGiving, isCryptoGiving, isStockGiving } = useMemo(
		() => ({
			isCardGiving: givingType === GivingType.CARD_OR_ACH,
			isCryptoGiving: givingType === GivingType.CRYPTOCURRENCY,
			isStockGiving: givingType === GivingType.STOCK,
			hasOtherGiving: CryptoLink || StockGivingLink,
		}),
		[CryptoLink, StockGivingLink, givingType]
	);

	const formSubmit = useCallback(
		(data: WidgetInputs, isApplePay?: boolean) => {
			clearAppleError();
			if (isApplePay) {
				makeApplePayment(data, customFieldKeys);
			} else {
				onSubmit(data);
			}
		},
		[clearAppleError, customFieldKeys, makeApplePayment, onSubmit]
	);

	const { ref: amountRegisterRef, ...amountFieldProps } = register('Amount', fieldOptions.Amount);

	const setAmountInputRef = useCallback(
		(el: HTMLInputElement | null) => {
			amountRegisterRef(el);
			amountInputRef.current = el;

			const focusOnAmountInput =
				errors.Amount && errors.MultiFunds && Object.keys(errors.MultiFunds).length === 0;
			if (el && (shouldFocusAmountInputRef.current || focusOnAmountInput)) {
				if (shouldFocusAmountInputRef.current) {
					shouldFocusAmountInputRef.current = false;
				}

				el.focus();
				widgetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		},
		[amountRegisterRef, errors.Amount, errors.MultiFunds]
	);

	const clearSuccessAndFocusAmountInput = useCallback(() => {
		clearSuccess();
		shouldFocusAmountInputRef.current = true;
	}, [clearSuccess]);

	useEffect(() => {
		agent?.recordCustomEvent(WidgetId, {
			type: WidgetActions.successfullyLoaded,
			isApplePayAvailable: isAvailable,
		});
	}, [agent, isAvailable]);

	const recordCustomEvent = useCallback(
		(options: Record<string, any>) => {
			agent?.recordCustomEvent(WidgetId, {
				...options,
			});
		},
		[agent]
	);

	return (
		<FormProvider {...methods}>
			<div ref={widgetRef} className="widget">
				<div className="widget-content">
					{success !== null ? (
						<Success
							{...success}
							currency={CurrencyCode.toString()}
							dismiss={clearSuccessAndFocusAmountInput}
							merchant={ApplePayMerchantName || LegalName || handle}
							minHeight={widgetRef.current?.clientHeight ?? undefined}
							parentRef={widgetRef}
							paymentLabel={paymentLabel}
							policyUrl={config.DefaultSettings.PrivacyStatementUrl}
							symbol={CurrencySymbol.toString()}
							termsUrl={config.DefaultSettings.TermsAndConditionsUrl}
						/>
					) : (
						<form className="widget-form" noValidate onSubmit={handleSubmit(data => formSubmit(data))}>
							<div ref={scrollableRef} className="scollable-content">
								<h3 className="widget-form-title">
									{translate('title', { action: paymentLabel.VerbSentenceCase })}
								</h3>
								<AmountField
									currencyCode={CurrencyCode.toString()}
									currencySymbol={CurrencySymbol}
									id="amountInput"
									invalid={Boolean(errors.Amount)}
									{...amountFieldProps}
									ref={setAmountInputRef}
									errorMessage={errorMessages.Amount}
									readOnly={!!currentSelection.multiFunds?.length}
									title={translate('amountText', { label: paymentLabel.VerbLowerCase })}
									required
								/>
								{hasOtherGiving && (
									<SelectField
										acceptanceTestTargetId="givingType-field"
										id="givingTypeInput"
										options={givingTypeOptions}
										title={translate('givingType.title', { label: paymentLabel.VerbLowerCase })}
										value={givingType.toString()}
										required
										onChange={onGivingChange}
									/>
								)}
								{hasOtherGiving && !isCardGiving && (
									<EngivenGiving paymentLabel={paymentLabel} type={givingType} />
								)}
								{isCardGiving && (
									<div>
										<Recurring
											endDateField={{
												...register('RecurringEndDate', fieldOptions.RecurringEndDate),
												min: (
													fieldOptions.RecurringEndDate?.min as ValidationValueMessage<string>
												).value,
												errorMessage: errorMessages.RecurringEndDate,
											}}
											endType={currentSelection.endType}
											endTypeField={{
												...register('RecurringEndType', fieldOptions.RecurringEndType),
												options: endTypeOptions,
												errorMessage: errorMessages.RecurringEndType,
											}}
											frequency={currentSelection.recurrence}
											frequencyField={{
												...register('Recurrence', fieldOptions.Recurrence),
												options: frequencyOptions,
												errorMessage: errorMessages.Recurrence,
											}}
											giftType={currentSelection.giftType}
											giftTypeField={{
												...register('GiftType', fieldOptions.GiftType),
											}}
											occurrencesField={{
												...register(
													'RecurringEndOccurence',
													fieldOptions.RecurringEndOccurence
												),
												errorMessage: errorMessages.RecurringEndOccurence,
											}}
											paymentLabel={paymentLabel}
											showEndFields={PayerSpecifiedRecurringEndDateEnabled}
											startDate={currentSelection.recurringStartDate}
											startField={{
												...register('RecurringStartDate', fieldOptions.RecurringStartDate),
												min: dateFieldOptions.min,
												errorMessage: errorMessages.RecurringStartDate,
												title: currentSelection.recurringStartDateIsToday
													? translate('recurring.startingToday')
													: translate('recurring.starting'),
											}}
										/>
										{(!currentSelection.multiFunds || currentSelection.multiFunds.length <= 1) && (
											<SelectField
												id="fundKeyOrName"
												invalid={Boolean(errors.FundKeyOrName)}
												options={funds}
												{...register('FundKeyOrName', fieldOptions.FundKeyOrName)}
												errorMessage={errorMessages.FundKeyOrName}
												readOnly={funds.length === 1}
												required={funds.length !== 1}
												title={FundCustomLabel || translate('funds.toFund')}
											/>
										)}
										{SupportsMultiFundGiving && (
											<LineItems
												currencySymbol={CurrencySymbol}
												defaultFundKey={DefaultFundKey}
												fieldOptions={{
													Amount: fieldOptions.Amount,
													FundKeyOrName: fieldOptions.FundKeyOrName,
												}}
												options={funds}
											/>
										)}
										<CustomFields
											customFieldOptions={fieldOptions}
											customFields={config.CustomFields}
											errorMessages={errorMessages}
											register={register}
										/>
										{OneTimeFutureGiftEnabled &&
											currentSelection.giftType === GiftType.ONE_TIME && (
												<TextField
													{...register('GiftDate', fieldOptions.GiftDate)}
													errorMessage={errorMessages.GiftDate}
													id="gift-date-input"
													invalid={Boolean(errors.GiftDate)}
													min={dateFieldOptions.min}
													title={
														currentSelection.giftDateIsToday
															? translate('futureGiftLabelToday')
															: translate('futureGiftLabel')
													}
													type={dateFieldOptions.type}
												/>
											)}
									</div>
								)}
							</div>
							<div className={clsx('form-footer', isOverflow && 'box-shadow')}>
								{isCardGiving && <Button label="Next" type="submit" />}
								{isCryptoGiving && (
									<Link
										href={CryptoLink ?? ''}
										asButton
										openInNewTab
										onClick={() =>
											recordCustomEvent({
												type: WidgetActions.cryptoGivingClicked,
											})
										}
									>
										{translate('engiven.cryptoCta', { label: paymentLabel.NounLowerCase })}
									</Link>
								)}
								{isStockGiving && (
									<Link
										href={StockGivingLink ?? ''}
										asButton
										openInNewTab
										onClick={() =>
											recordCustomEvent({
												type: WidgetActions.stockGivingClicked,
											})
										}
									>
										{translate('engiven.stockCta', { label: paymentLabel.NounLowerCase })}
									</Link>
								)}
								{isAvailable && currentSelection.showApplePay && isCardGiving && (
									<div className="apple-pay-wrapper">
										<div className="separator-wrapper">
											<Separator />
											or
											<Separator />
										</div>
										<ApplePayButton onClick={handleSubmit(data => formSubmit(data, true))} />
									</div>
								)}
								{applePayError && (
									<ErrorMessage
										subtitle={applePayError.message.subtitle}
										title={applePayError.message.title}
									/>
								)}
								<PoweredByPushpay />
								<TermsAndPolicy
									label={paymentLabel.NounLowerCase}
									name={LegalName || ApplePayMerchantName || handle}
									policyUrl={config.DefaultSettings.PrivacyStatementUrl}
									termsUrl={config.DefaultSettings.TermsAndConditionsUrl}
								/>
							</div>
						</form>
					)}
				</div>
			</div>
		</FormProvider>
	);
};
